import { useState } from 'react';
import { SiteSettings } from '@/hooks/useSiteSettings';

interface SiteSettingsEditorProps {
  settings: SiteSettings;
  onSave: (s: SiteSettings) => Promise<boolean>;
}

export default function SiteSettingsEditor({ settings, onSave }: SiteSettingsEditorProps) {
  const [form, setForm] = useState<SiteSettings>({ ...settings });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (field: keyof SiteSettings, value: string | number) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(form);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleReset = () => {
    setForm({ ...settings });
    setSaved(false);
  };

  const socialFields: { key: keyof SiteSettings; label: string; icon: string; placeholder: string }[] = [
    { key: 'instagramUrl', label: 'Instagram', icon: 'ri-instagram-line', placeholder: 'https://instagram.com/seuperfil' },
    { key: 'facebookUrl', label: 'Facebook', icon: 'ri-facebook-circle-line', placeholder: 'https://facebook.com/suapagina' },
    { key: 'pinterestUrl', label: 'Pinterest', icon: 'ri-pinterest-line', placeholder: 'https://pinterest.com/seuperfil' },
    { key: 'youtubeUrl', label: 'YouTube', icon: 'ri-youtube-line', placeholder: 'https://youtube.com/@seucanal' },
  ];

  const filledCount = socialFields.filter(f => form[f.key]).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Identidade Visual
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Configure a logo, nome e subtítulo do site.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i> Salvando...</>
            ) : saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar</>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Branding section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left — Branding Text */}
          <div className="space-y-5">
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5">
              <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
                <i className="ri-text text-primary-500"></i>
                Textos
              </h3>

              {/* Site Name */}
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  Nome do Site
                </label>
                <input
                  type="text"
                  value={form.siteName}
                  onChange={e => update('siteName', e.target.value)}
                  placeholder="Ex: miaudecasa"
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                />
              </div>

              {/* Site Subtitle */}
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  Subtítulo
                </label>
                <input
                  type="text"
                  value={form.siteSubtitle}
                  onChange={e => update('siteSubtitle', e.target.value)}
                  placeholder="Ex: subtítulo do site"
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                />
              </div>
            </div>

            {/* Favicon */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
              <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
                <i className="ri-image-2-line text-primary-500"></i>
                Favicon
              </h3>
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  URL do Favicon
                </label>
                <input
                  type="text"
                  value={form.faviconUrl}
                  onChange={e => update('faviconUrl', e.target.value)}
                  placeholder="https://..."
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                />
              </div>
              {form.faviconUrl && (
                <div>
                  <p className="text-xs text-foreground-400 mb-2">Preview:</p>
                  <div className="w-12 h-12 rounded-lg overflow-hidden border border-background-200">
                    <img
                      src={form.faviconUrl}
                      alt="Favicon preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right — Logo */}
          <div className="space-y-5">
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
              <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
                <i className="ri-image-line text-primary-500"></i>
                Logo
              </h3>
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  URL da Logo
                </label>
                <input
                  type="text"
                  value={form.logoUrl}
                  onChange={e => update('logoUrl', e.target.value)}
                  placeholder="https://..."
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                />
              </div>
              {form.logoUrl && (
                <div>
                  <p className="text-xs text-foreground-400 mb-2">Preview:</p>
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-primary-500">
                    <img
                      src={form.logoUrl}
                      alt="Logo preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Live Preview */}
            <div className="bg-background-100 border border-background-200 rounded-xl p-5">
              <h3 className="font-bold text-foreground-950 text-sm mb-3 flex items-center gap-2">
                <i className="ri-eye-line text-primary-500"></i>
                Pré-visualização
              </h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-500 flex items-center justify-center flex-shrink-0 bg-white">
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-heart-fill text-primary-500 text-xl"></i>
                  )}
                </div>
                <div>
                  <div className="font-heading font-bold text-primary-500 text-base leading-none">
                    {form.siteName || 'miaudecasa'}
                  </div>
                  <div className="text-foreground-500 text-xs">
                    {form.siteSubtitle || 'subtítulo do site'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
              <i className="ri-share-line text-primary-500"></i>
              Redes Sociais do Rodapé
            </h3>
            <span className="text-xs text-foreground-400">
              {filledCount}/4 preenchidas
            </span>
          </div>
          <p className="text-foreground-500 text-xs -mt-3">
            Links exibidos nos ícones do rodapé. Deixe vazio para ocultar.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {socialFields.map(sf => (
              <div key={sf.key}>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <i className={`${sf.icon} text-sm`}></i>
                    {sf.label}
                  </span>
                </label>
                <input
                  type="url"
                  value={form[sf.key] as string}
                  onChange={e => update(sf.key, e.target.value)}
                  placeholder={sf.placeholder}
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                />
                {form[sf.key] && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <i className="ri-check-line"></i> Link ativo no rodapé
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}